import { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';

import { IconButton, InlineButton } from '../../components/button';
import { Input } from '../../components/form/input';
import { Icon } from '../../components/icon';
import type {
  Aggregation,
  AggregationExtendedType,
  CompositeFilterCondition,
  Field,
  Metric,
  Property,
  Sort,
  VariableDefinition,
} from '../types';
import { AggregationForm } from './aggregation-form';
import { CompositeFilterConditionForm } from './edit-filter';
import { SortableItemSimple } from '../components/sortable-item';
import { ensureUniqueFieldKey, ensureUniqueKey, nameToKey } from './utils';
import { Dropdown, DropdownMenuItem, DropdownIconSizePx } from '../../components/dropdown';
import {
  AggregationOptions,
  aggregationTypeToAggregationExtendedType,
  buildDefaultAggregationFilter,
  buildMetricAggregation,
  changeAggregationType,
  generateAggregationProperty,
  getAggregationOptionLabelByType,
  getAggregationOptionsForFields,
  getDefaultAggregation,
} from '../utils/aggregation';

import { useEnsureFieldsExist, useEnsureFieldsExistGrouped } from './hooks/use-ensure-fields-exist';
import {
  getAggregationKeys,
  getAggregationPropertyKeys,
  getCompositeConditionKeys,
  isAggregationWithFilter,
  isDeduplicatableAggregation,
  isFilteringAggregation,
  isKeyedAggregation,
  isSortedAggregation,
} from '../pipeline/operation';
import { FieldOrGroup } from '../pipeline/utils';
import { metricById } from '../utils/metrics';

import form from '../../components/form/form.module.scss';

type CreateUnnamedAggregation<T extends Aggregation> = T & { __unnamed: true };
type UnnamedAggregation = CreateUnnamedAggregation<Aggregation>; // With default property.name

const isUnnamedAggregation = (
  aggregation: Aggregation | UnnamedAggregation,
): aggregation is UnnamedAggregation => {
  return '__unnamed' in aggregation;
};

export const convertToUnnamedAggregation = (aggregation: Aggregation): UnnamedAggregation => {
  return {
    ...aggregation,
    __unnamed: true,
  };
};

export const convertToNamedAggregation = (
  aggregation: Aggregation | UnnamedAggregation,
): Aggregation => {
  if (isUnnamedAggregation(aggregation)) {
    const { __unnamed, ...namedAggregation } = aggregation;
    return namedAggregation;
  }
  return aggregation;
};

const hasDefaultPropertyName = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
  fieldNameSuffix?: string,
) => {
  const defaultProperty = generateAggregationProperty(
    aggregation,
    existingFields,
    aggregationFields,
    metrics,
    fieldNameSuffix,
  );
  return aggregation.property.name === defaultProperty.name;
};

const aggregationToField = (aggregation: Aggregation): Field => ({
  key: aggregation.property.key,
  name: aggregation.property.name,
  type: null,
});

/**
 * Regenerate aggregation property keys/names after each change.
 * Considers existing fields and other aggregations for name disambiguation.
 */
export const regenerateAggregationProperties = (
  updatedAggregations: (Aggregation | UnnamedAggregation)[],
  initialAggregations: Aggregation[],
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
  fieldNameSuffix?: string,
) => {
  // Regenerate aggregation property keys/names after each change
  return updatedAggregations.reduce((acc, agg) => {
    const initialAggregation = initialAggregations.find((a) => a.property.key === agg.property.key);
    const existingFieldsForAggregation = [...existingFields, ...acc.map(aggregationToField)];
    const newDefaultProperty = generateAggregationProperty(
      agg,
      existingFieldsForAggregation,
      aggregationFields,
      metrics,
      fieldNameSuffix,
    );
    // Never change key of already saved aggregations
    // Only change name if it was default before (marked as unnamed)
    const canChangeKey = initialAggregation === undefined;
    const canChangeKeyToNewDefault = canChangeKey && isUnnamedAggregation(agg);
    const canChangeNameToNewDefault = isUnnamedAggregation(agg);
    acc.push({
      ...agg,
      property: {
        key: canChangeKeyToNewDefault
          ? newDefaultProperty.key
          : canChangeKey
            ? ensureUniqueFieldKey(existingFieldsForAggregation, nameToKey(agg.property.name))
            : initialAggregation.property.key,
        name: canChangeNameToNewDefault ? newDefaultProperty.name : agg.property.name,
      },
    });
    return acc;
  }, [] as Aggregation[]);
};

interface AggregationsEditorProps {
  aggregations: Aggregation[];
  existingFields: Field[]; // For name disambiguation
  aggregationFields: Field[]; // Fields available for aggregation
  filterFields: FieldOrGroup[]; // Fields available for filtering
  metrics: Metric[];
  fieldNameSuffix?: string; // For name disambiguation
  showNameInput?: boolean;
  setAggregations(aggregations: Aggregation[]): void;
  // If no empty state is provided, the editor will always show at least one aggregation
  empty?: (addAggregation: (aggregation: Aggregation) => void) => React.ReactNode;
  autoFocus?: boolean;
  excludeAggregationTypes?: AggregationExtendedType[];
  getMenuOptions?: (aggregation: Aggregation, idx: number) => DropdownMenuItem[];
  filterEnabled?: boolean;
  variableDefinitions: VariableDefinition[];
}

export const AggregationsEditor = (props: AggregationsEditorProps) => {
  const {
    existingFields,
    aggregationFields,
    filterFields,
    metrics,
    excludeAggregationTypes = [],
    fieldNameSuffix,
    autoFocus = false,
    getMenuOptions,
  } = props;

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null);
  const [initialAggregations] = useState<Aggregation[]>(props.aggregations);

  const paddedAggregationFields = useEnsureFieldsExist(
    aggregationFields,
    getAggregationKeys(props.aggregations),
  );

  const paddedFilterFields = useEnsureFieldsExistGrouped(
    filterFields,
    props.aggregations.flatMap((agg) =>
      isFilteringAggregation(agg) && agg.filter ? getCompositeConditionKeys(agg.filter) : [],
    ),
  );

  // To get unique names, we need to consider other aggregations' property names
  // as well as all existing fields
  const getExistingFieldsForAggregation = (aggregation: Aggregation) => [
    ...existingFields,
    ...props.aggregations
      .filter((agg) => agg.property.key !== aggregation.property.key)
      .map(aggregationToField),
  ];

  const aggregations = props.aggregations.map((agg) => {
    return hasDefaultPropertyName(
      agg,
      getExistingFieldsForAggregation(agg),
      paddedAggregationFields,
      metrics,
      fieldNameSuffix,
    )
      ? convertToUnnamedAggregation(agg)
      : agg;
  });

  const setAggregations = (updatedAggregations: (Aggregation | UnnamedAggregation)[]) => {
    props.setAggregations(
      regenerateAggregationProperties(
        updatedAggregations,
        initialAggregations,
        existingFields,
        paddedAggregationFields,
        metrics,
        fieldNameSuffix,
      ).map((agg) => convertToNamedAggregation(agg)),
    );
  };

  const handleSetAggregation = (idx: number, aggregation: Aggregation) =>
    setAggregations([...aggregations.slice(0, idx), aggregation, ...aggregations.slice(idx + 1)]);

  const handleAddAggregation = (aggregation: Aggregation) => {
    const existingKeys = getAggregationPropertyKeys(aggregations);
    setAggregations([
      ...aggregations,
      {
        ...convertToUnnamedAggregation(aggregation),
        property: {
          ...aggregation.property,
          key: ensureUniqueKey(existingKeys, aggregation.property.key),
        },
      },
    ]);
  };

  const handleRemoveAggregation = (index: number) => {
    setAggregations([...aggregations.slice(0, index), ...aggregations.slice(index + 1)]);

    setHighlightedIdx(null);
  };

  const handleDuplicateAggregation = (index: number) => {
    const aggregation = aggregations.at(index);
    if (!aggregation) {
      return;
    }
    console.log('🔹 . handleDuplicateAggregation . aggregation:', aggregation);

    const duplicatedAggregation = convertToNamedAggregation({
      ...aggregation,
      property: {
        ...aggregation.property,
        key: ensureUniqueKey(getAggregationPropertyKeys(aggregations), aggregation.property.key),
        name: aggregation.property.name ? `${aggregation.property.name} copy` : '',
      },
    });
    console.log('🔹 . handleDuplicateAggregation . duplicatedAggregation:', duplicatedAggregation);

    setAggregations([
      ...aggregations.slice(0, index + 1),
      duplicatedAggregation,
      ...aggregations.slice(index + 1),
    ]);

    setHighlightedIdx(index + 1);
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const tempAggregations = [
      ...aggregations.slice(0, fromIndex),
      ...aggregations.slice(fromIndex + 1),
    ];
    setAggregations([
      ...tempAggregations.slice(0, toIndex),
      aggregations[fromIndex],
      ...tempAggregations.slice(toIndex),
    ]);

    setHighlightedIdx(null);
  };

  useLayoutEffect(() => {
    if (props.aggregations.length === 0 && props.empty === undefined) {
      handleAddAggregation(
        getDefaultAggregation(
          existingFields,
          aggregationFields,
          metrics,
          fieldNameSuffix,
          getAggregationOptionsForFields(aggregationFields),
        ),
      );
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.aggregations]);

  if (aggregations.length === 0 && props.empty !== undefined) {
    return props.empty(handleAddAggregation);
  }

  const sortable = aggregations.length > 1;

  return (
    <div className={form.formHorizontal}>
      {aggregations.map((aggregation, i, items) => (
        <Fragment key={i}>
          {i > 0 && <hr className={form.dashed} />}
          <SortableItemSimple
            scope={'aggregation'}
            index={i}
            draggedIndex={draggedIndex}
            setDraggedIndex={setDraggedIndex}
            onReorder={handleReorder}
            className={classNames({ [form.sortableRow]: sortable })}
            enabled={sortable}
            classNames={{
              dragBefore: form.dragBefore,
              dragAfter: form.dragAfter,
            }}>
            {sortable && <Icon name="DragHandle" size={10} className={form.sortHandle} />}
            <EditAggregation
              aggregation={aggregation}
              aggregationFields={paddedAggregationFields}
              filterFields={paddedFilterFields}
              metrics={metrics}
              autoFocus={highlightedIdx !== null ? highlightedIdx === i : autoFocus}
              isRemovable={aggregations.length > 1 || props.empty !== undefined}
              isHighlighted={highlightedIdx === i}
              showNameInput={props.showNameInput}
              defaultProperty={generateAggregationProperty(
                aggregation,
                getExistingFieldsForAggregation(aggregation),
                paddedAggregationFields,
                metrics,
                fieldNameSuffix,
              )}
              setAggregation={(aggregation) => handleSetAggregation(i, aggregation)}
              onRemove={() => handleRemoveAggregation(i)}
              onDuplicateAggregation={() => handleDuplicateAggregation(i)}
              excludeAggregationTypes={excludeAggregationTypes}
              menuOptions={getMenuOptions?.(aggregation, i)}
              filterEnabled={props.filterEnabled}
              variableDefinitions={props.variableDefinitions}
            />
            {aggregation === items.at(-1) && isAggregationWithFilter(aggregation) && (
              <hr className={form.dashed} style={{ margin: 0 }} />
            )}
          </SortableItemSimple>
        </Fragment>
      ))}

      <div>
        <InlineButton
          size="small"
          onClick={() => {
            const unusedAggregationOptions = AggregationOptions.filter(
              (option) =>
                aggregations.every((agg) => {
                  const field = isKeyedAggregation(agg)
                    ? aggregationFields.find((f) => f.key === agg.key)
                    : undefined;
                  const type = aggregationTypeToAggregationExtendedType(agg.type, field);
                  return type !== option.value;
                }) && !excludeAggregationTypes.includes(option.value),
            );
            handleAddAggregation(
              getDefaultAggregation(
                [...props.existingFields, ...aggregations.map(aggregationToField)],
                aggregationFields,
                metrics,
                fieldNameSuffix,
                unusedAggregationOptions,
              ),
            );
          }}>
          <Icon name="Plus" size={15} /> Summarization
        </InlineButton>
      </div>
    </div>
  );
};

interface EditAggregationProps {
  aggregation: Aggregation | UnnamedAggregation;
  aggregationFields: Field[];
  filterFields: FieldOrGroup[];
  metrics: Metric[];
  isRemovable: boolean;
  isHighlighted?: boolean;
  setAggregation(aggregation: Aggregation | UnnamedAggregation): void;
  defaultProperty: Property;
  showNameInput?: boolean;
  onDuplicateAggregation(): void;
  onRemove?(): void;
  excludeAggregationTypes?: AggregationExtendedType[];
  autoFocus?: boolean;
  menuOptions?: DropdownMenuItem[];
  filterEnabled?: boolean;
  variableDefinitions: VariableDefinition[];
}

const EditAggregation = (props: EditAggregationProps) => {
  const {
    aggregation,
    aggregationFields,
    filterFields,
    metrics,
    isRemovable,
    isHighlighted = false,
    defaultProperty,
    showNameInput,
    excludeAggregationTypes = [],
    autoFocus = false,
    onRemove,
    setAggregation,
    menuOptions = [],
    filterEnabled = false,
  } = props;

  const isUnnamed = isUnnamedAggregation(aggregation);

  const [name, setName] = useState(aggregation.property.name);
  const [manualPropertyEnabled, setManualPropertyEnabled] = useState(!isUnnamed);

  const shouldShowNameInput = showNameInput === true || manualPropertyEnabled || !isUnnamed;

  const menuItems: DropdownMenuItem[] = [...menuOptions];

  useEffect(() => {
    if (aggregation.property.name !== name) {
      // Keep state in sync when props change (eg. aggregation duplication)
      // Cannot use key to thrash component since name changes are emitted on every keystroke
      setName(aggregation.property.name);
    }
  }, [aggregation.property.name, name]);

  if (!shouldShowNameInput) {
    menuItems.push({
      label: 'Add custom name',
      onClick: () => setManualPropertyEnabled(true),
      icon: <Icon name="Edit3" size={DropdownIconSizePx} />,
    });
  }

  if (filterEnabled) {
    if (isAggregationWithFilter(aggregation)) {
      menuItems.push({
        label: 'Remove filter',
        onClick: () => setAggregation({ ...aggregation, filter: undefined }),
        icon: <Icon name="Filter" size={DropdownIconSizePx} />,
      });
    } else {
      menuItems.push({
        label: 'Add filter',
        disabled: !isFilteringAggregation(aggregation),
        tooltip: !isFilteringAggregation(aggregation)
          ? `Filter is not supported for ${getAggregationOptionLabelByType(aggregation.type) ?? aggregation.type} summarization`
          : undefined,
        onClick: () => {
          if (!isFilteringAggregation(aggregation)) {
            return;
          }

          setAggregation({
            ...aggregation,
            filter: buildDefaultAggregationFilter(aggregation, filterFields),
          });
        },
        icon: <Icon name="Filter" size={DropdownIconSizePx} />,
      });
    }
  }

  menuItems.push({
    label: 'Duplicate summarization',
    onClick: props.onDuplicateAggregation,
    icon: <Icon name="Copy" size={DropdownIconSizePx} />,
  });

  if (isRemovable) {
    menuItems.push({
      label: 'Remove summarization',
      onClick: onRemove,
      icon: <Icon name="Trash2" size={DropdownIconSizePx} />,
    });
  }

  const handleAggregationChange = (updatedAggregation: Aggregation) => {
    if (isFilteringAggregation(aggregation) && isFilteringAggregation(updatedAggregation)) {
      updatedAggregation.filter = aggregation.filter;
    }

    setAggregation(updatedAggregation);
  };

  const handleAggregationTypeChange = (type: AggregationExtendedType) => {
    const metric = metricById(metrics, type);
    if (metric !== undefined) {
      setAggregation(buildMetricAggregation(metric));
      return;
    }
    const updatedAggregation = changeAggregationType(aggregation, type, aggregationFields);
    handleAggregationChange(
      isUnnamedAggregation(aggregation)
        ? convertToUnnamedAggregation(updatedAggregation)
        : convertToNamedAggregation(updatedAggregation),
    );
  };

  const handleAggregationFieldChange = (key: string) => {
    if (!isKeyedAggregation(aggregation)) {
      throw new Error(`Cannot change field of aggregation type ${aggregation.type}`);
    }
    handleAggregationChange({
      ...aggregation,
      key,
    });
  };

  const handleAggregationSortingChange = (sort: Sort) => {
    if (!isSortedAggregation(aggregation)) {
      throw new Error(`Cannot change sorting of aggregation type ${aggregation.type}`);
    }
    handleAggregationChange({
      ...aggregation,
      sort,
    });
  };

  const handleFilterChange = (filter: CompositeFilterCondition) => {
    if (isFilteringAggregation(aggregation)) {
      setAggregation({ ...aggregation, filter });
    }
  };

  const handleNameChange = (value: string) => {
    const propertyName = !value.length ? defaultProperty.name : value;
    setName(value);
    const property = {
      key: aggregation.property.key,
      name: propertyName,
    };
    if (value.length) {
      setAggregation({
        ...convertToNamedAggregation(aggregation),
        property,
      });
    } else {
      setAggregation({
        ...convertToUnnamedAggregation(aggregation),
        property,
      });
    }
  };

  const handleAggregationDistinctChange = (distinct: boolean) => {
    if (!isDeduplicatableAggregation(aggregation)) {
      throw new Error(`Cannot change distinct of aggregation type ${aggregation.type}`);
    }

    handleAggregationChange({
      ...aggregation,
      distinct: distinct ? true : undefined,
    });
  };

  const menu =
    menuItems.length === 1 && isRemovable ? (
      <IconButton icon="Trash2" title="Remove aggregation" size="small" onClick={onRemove} />
    ) : (
      menuItems.length > 0 && (
        <Dropdown
          align="right"
          trigger={(isOpen, setIsOpen) => (
            <IconButton
              icon="MoreHorizontal"
              size="small"
              title="More..."
              type="gray"
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
          items={menuItems}
        />
      )
    );

  return (
    <div className={classNames(form.formHorizontal, { [form.highlightByFadeIn]: isHighlighted })}>
      <AggregationForm
        accessory={menu}
        aggregation={aggregation}
        autoFocus={autoFocus}
        excludeAggregationTypes={excludeAggregationTypes}
        fields={aggregationFields}
        metrics={metrics}
        onChangeType={handleAggregationTypeChange}
        onChangeField={handleAggregationFieldChange}
        onChangeSorting={handleAggregationSortingChange}
        onChangeDistinct={handleAggregationDistinctChange}
      />
      {shouldShowNameInput && (
        <div className={classNames(form.formRow)}>
          <label className={form.formLabel}>Column name</label>
          <Input
            type="text"
            autoFocus={autoFocus}
            value={isUnnamed ? '' : name}
            placeholder={aggregation.property.name}
            onChange={(e) => {
              handleNameChange(e.target.value);
            }}
          />
        </div>
      )}

      {isAggregationWithFilter(aggregation) && (
        <>
          <label className={form.formLabel}>Filter before aggregating</label>
          <CompositeFilterConditionForm
            fields={filterFields}
            fieldsForExpression={aggregationFields}
            condition={aggregation.filter}
            setCondition={handleFilterChange}
            variables={props.variableDefinitions}
          />
        </>
      )}
    </div>
  );
};
